import type { DOMProps } from '@react-types/shared';
import type { PropsWithChildren } from 'react';
import { Fragment } from 'react';
import { createPortal } from 'react-dom';

import { typedKeys } from '@infinex-private/ts-utils/object';
import { useIsSSR } from '@infinex/ui-utils/react-dom';

import {
  CHAIN_SVG_SYMBOLS,
  DEFUSE_SVG_SYMBOLS,
  makeSymbolId,
} from './constants';

/**
 * Renders a single SVG containing all chain symbols, within a portal at the end
 * of the document body.
 */
export function ChainSvgDefs() {
  if (useIsSSR()) {
    // Render inline during SSR so symbols are available immediately
    return <ChainSymbols />;
  }

  // Portal to document.body on client for performance
  return createPortal(<ChainSymbols />, document.body);
}

function ChainSymbols() {
  return (
    <svg
      aria-hidden
      focusable="false"
      tabIndex={-1}
      height={0}
      width={0}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {typedKeys(CHAIN_SVG_SYMBOLS).map((chain) => {
          const svgElements = CHAIN_SVG_SYMBOLS[chain];
          return (
            <Fragment key={chain}>
              <Symbol id={makeSymbolId(chain, 'emblemSymbol')}>
                {svgElements.emblemSymbol}
              </Symbol>
              <Symbol id={makeSymbolId(chain, 'glyphSymbol')}>
                {svgElements.glyphSymbol}
              </Symbol>
              <Symbol id={makeSymbolId(chain, 'glyphMonoSymbol')}>
                {svgElements.glyphMonoSymbol}
              </Symbol>
            </Fragment>
          );
        })}
        {typedKeys(DEFUSE_SVG_SYMBOLS).map((chain) => {
          const svgElements = DEFUSE_SVG_SYMBOLS[chain];
          return (
            <Fragment key={chain}>
              <Symbol id={makeSymbolId(chain, 'emblemSymbol')}>
                {svgElements.emblemSymbol}
              </Symbol>
              <Symbol id={makeSymbolId(chain, 'glyphSymbol')}>
                {svgElements.glyphSymbol}
              </Symbol>
              <Symbol id={makeSymbolId(chain, 'glyphMonoSymbol')}>
                {svgElements.glyphMonoSymbol}
              </Symbol>
            </Fragment>
          );
        })}
      </defs>
    </svg>
  );
}

function Symbol(props: PropsWithChildren<DOMProps>) {
  return <symbol viewBox="0 0 24 24" height={24} width={24} {...props} />;
}
